import React from 'react';
import Paper from '@material-ui/core/Paper';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import { t } from 'ttag';
import MaterialTable from 'material-table';
import { MyClubCApi } from '../../../api';
import { TabPanel, useTabs } from '../../../components/Tab/Tabs';
import { localization } from '../../../components/DataTable/utils';
import { useNavigator } from '../../../components/Navigator';
import { useApolloClient } from '@apollo/react-hooks';
import { getBasePath } from '../../../utils/urls';
import useUser from '../../../auth/hooks';
import { canEditDankai, canViewDankai } from '../../../utils/permissions';

const sortDankaiByNameDesc = (list = []) =>
  [...list].sort((left, right) => {
    const leftRank = parseInt(left?.name, 10);
    const rightRank = parseInt(right?.name, 10);

    if (!Number.isNaN(leftRank) && !Number.isNaN(rightRank)) {
      return rightRank - leftRank;
    }

    return String(right?.name || '').localeCompare(String(left?.name || ''));
  });

const TABS = {
  kumite: 0,
  kata: 1,
};

const TAB_TYPES = {
  [TABS.kumite]: 'kumite',
  [TABS.kata]: 'kata',
};

const ListView = () => {
  const navigate = useNavigator();
  const client = useApolloClient();
  const newApi = React.useMemo(() => new MyClubCApi(client), [client]);
  const [tab, onChangeTab] = useTabs({ tabs: TABS, defaultTab: TABS.kumite });
  const [dankaiMap, setDankaiMap] = React.useState({});
  const [loadingType, setLoadingType] = React.useState(null);
  const topRef = React.useRef(null);
  const { user } = useUser();
  const ctxName = getBasePath(user)

  const tabConfig = [
    {
      index: TABS.kumite,
      label: t`Куміте`,
      type: TAB_TYPES[TABS.kumite],
      title: t`Список куміте КЮ`,
    },
    {
      index: TABS.kata,
      label: t`Ката`,
      type: TAB_TYPES[TABS.kata],
      title: t`Список ката КЮ `,
    },
  ];

  const activeTab = tabConfig.find((item) => item.index === tab);
  const activeType = activeTab ? activeTab.type : undefined;
  const hasDataForActiveType = activeType
    ? Boolean(dankaiMap[activeType])
    : false;

  React.useEffect(() => {
    const requestType = activeType;
    if (!requestType) {
      return;
    }
    if (hasDataForActiveType) {
      return;
    }

    let isCancelled = false;
    const fetchDankai = async () => {
      setLoadingType(requestType);
      try {
        const response = await newApi.getDankais({ type: requestType });
        if (!isCancelled) {
          setDankaiMap((prev) => ({
            ...prev,
            [requestType]: sortDankaiByNameDesc(response || []),
          }));
        }
      } catch (err) {
        if (!isCancelled) {
          // eslint-disable-next-line no-console
          console.error('Failed to load dankai list', err);
          setDankaiMap((prev) => ({
            ...prev,
            [requestType]: [],
          }));
        }
      } finally {
        if (!isCancelled) {
          setLoadingType((current) =>
            current === requestType ? null : current
          );
        }
      }
    };

    fetchDankai();

    return () => {
      isCancelled = true;
      setLoadingType((current) => (current === requestType ? null : current));
    };
  }, [newApi, activeType, hasDataForActiveType]);

  const columns = [
    { title: t`ID`, field: 'id', editable: 'never' },
    { title: t`Назва KЮ`, field: 'name' },
  ];

  const renderTable = (config) => (
    <MaterialTable
      title={config.title}
      columns={columns}
      localization={localization}
      options={{
        search: false,
        actionsColumnIndex: -1,
        pageSize: 10,
        paging: false,
      }}
      data={dankaiMap[config.type] || []}
      isLoading={tab === config.index && loadingType === config.type}
      actions={[
        (rowData) => ({
          icon: 'edit',
          tooltip: t`Редагувати`,
          disabled: !canEditDankai(user),
          onClick: (event, data) => {
            if (!data) {
              return;
            }
            navigate(`/${ctxName}/dankai/${config.type}/${data.id}/edit`);
          },
        }),
        (rowData) => ({
          icon: 'visibility',
          tooltip: t`Переглянути`,
          disabled: !canViewDankai(user),
          onClick: (event, data) => {
            if (!data) {
              return;
            }
            navigate(`/${ctxName}/dankai/${config.type}/${data.id}`);
          },
        }),
      ]}
    />
  );

  return (
    <>
      <Typography variant="h5">{t`Данкай`}</Typography>
      <br />
      <Paper>
        <Tabs
          ref={topRef}
          value={tab}
          indicatorColor='primary'
          textColor='primary'
          onChange={(e, v) => onChangeTab(v)}
        >
          {tabConfig.map((item) => (
            <Tab key={item.type} label={item.label} />
          ))}
        </Tabs>
        {tabConfig.map((item) => (
          <TabPanel key={item.type} value={tab} index={item.index}>
            {renderTable(item)}
          </TabPanel>
        ))}
      </Paper>
    </>
  );
};

export default ListView;
